import { DropdownOptionProps } from '@appsumo/dorado-react';
import * as Yup from 'yup';

import { isABNValid, isPostalCodeValid } from '~/lib/validators';
import { isTrue } from '~/lib/util/helpers';
import { COUNTRIES_REQUIRE_POSTAL_CODE } from '~/constants/checkout';

function abnSchemaTest(schema: Yup.StringSchema) {
  return schema.test(
    'test-abn-format',
    'Invalid ABN format, it should be in the form of 12 345 678 910 or 12345678910',
    function (value) {
      return isABNValid(value ?? '');
    },
  );
}

const billingSchemaObject = {
  save_billing_info: Yup.boolean(),
  country: Yup.object().required(),
  postal_code: Yup.string().when('country', {
    is: (country: DropdownOptionProps) =>
      COUNTRIES_REQUIRE_POSTAL_CODE?.includes(`${country?.value}`),
    then: (schema) =>
      schema.test(
        'test-postal-code-format',
        'Invalid postal code format',
        function (value, ctx) {
          const currentCountry = ctx?.parent?.country?.value;
          const isValidPostalCode = isPostalCodeValid(
            value ?? '',
            currentCountry,
          );
          let message = 'Invalid postal code.';

          if (!isValidPostalCode) {
            switch (currentCountry) {
              case 'GB':
                message += ' UK postal code must be of form AA11 1AA.';
                break;
              case 'CA':
                message += ' Canadian postal code must be of form A1A 1A1.';
                break;
              case 'US':
                message +=
                  ' US postal code must be of form 12345 or 12345-1234.';
                break;
              default:
                break;
            }

            return ctx.createError({ message });
          }

          return true;
        },
      ),
    otherwise: (schema) => schema.min(0),
  }),
  billing_name: Yup.string().max(
    255,
    "Business name can't be longer than 255 characters.",
  ),
};

const taxSchemaObject = {
  save_tax_info: Yup.string(), // Not a boolean since it's a hidden field with a string value
  tax_id: Yup.string().when('save_tax_info', {
    is: (saveTaxInfo: string) => isTrue(saveTaxInfo),
    then: (schema) => abnSchemaTest(schema),
    otherwise: (schema) => schema.min(0),
  }),
  tax_business_name: Yup.string().when('save_tax_info', {
    is: (saveTaxInfo: string) => isTrue(saveTaxInfo),
    then: (schema) =>
      schema.required('A business name must be associated with a tax ID.'),
    otherwise: (schema) => schema.min(0),
  }),
};

export const accountTaxSchema = Yup.object({
  country: Yup.mixed<DropdownOptionProps>()
    .defined()
    .test({
      test(option, ctx) {
        if (!option?.value) {
          return ctx.createError({
            message: 'Please select the country where your business is located',
          });
        }

        return true;
      },
    })
    .required(),
  tax_id: Yup.string().when('country', {
    is: (country: DropdownOptionProps) => country.value === 'AU',
    then: (schema) => abnSchemaTest(schema),
    otherwise: (schema) => schema.min(1, 'Please add your tax ID'),
  }),
  tax_business_name: Yup.string()
    .min(1, 'Please add your business name')
    .required(),
}).required();

export const completeBillingSchema = Yup.object({
  ...billingSchemaObject,
  ...taxSchemaObject,
}).required();

export const billingSchema = Yup.object(billingSchemaObject).required();
